<script setup lang="ts">
/**
 * Code aus der Authenticator-App für einen heiklen Schritt (Ausbau H).
 *
 * Öffnet sich von selbst, wenn ein Aufruf über `useBestaetigungStore().mit()`
 * die Antwort `bestaetigung_noetig` bekommt. Nach dem Code läuft der Aufruf
 * weiter; zehn Minuten lang fragt der Server danach nicht wieder.
 */
import { nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ApiError, api } from '@/lib/api'
import { useEntwurf } from '@/composables/useEntwurf'
import { useBestaetigungStore } from '@/stores/bestaetigung'

const { t } = useI18n()
const store = useBestaetigungStore()
const code = ref('')
const laeuft = ref(false)
const fehler = ref<string | null>(null)
const feld = ref<HTMLInputElement | null>(null)

useEntwurf(() => store.offen)

watch(() => store.offen, async (offen) => {
  if (!offen) return
  code.value = ''
  fehler.value = null
  await nextTick()
  feld.value?.focus()
})

async function senden(): Promise<void> {
  const c = code.value.replace(/\s+/g, '')
  if (!/^\d{6}$/.test(c) || laeuft.value) return
  laeuft.value = true
  fehler.value = null
  try {
    await api.bestaetigen(c)
    store.bestaetigt()
  } catch (e) {
    const grund = e instanceof ApiError ? (e.body as { error?: unknown } | null)?.error : null
    fehler.value = grund === 'code_falsch'
      ? t('bestaetigung.falsch')
      : grund === 'rate_limited'
        ? t('bestaetigung.gesperrt')
        : grund === 'zwei_faktor_noetig'
          ? t('werkbank.zwei_faktor_zuerst')
          : t('bestaetigung.fehler')
    code.value = ''
  } finally {
    laeuft.value = false
  }
}
</script>

<template>
  <div
    v-if="store.offen"
    class="fixed inset-0 z-60 grid place-items-center bg-black/50 p-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="bestaetigung-titel"
    @keydown.esc="store.abgebrochen()"
  >
    <form class="w-full max-w-sm rounded-xl border border-line bg-surface p-5 shadow-xl" @submit.prevent="senden">
      <h2 id="bestaetigung-titel" class="text-sm font-semibold text-fg">{{ t('bestaetigung.titel') }}</h2>
      <p class="mt-1 text-xs text-fg-mid">{{ t('bestaetigung.text') }}</p>
      <label class="sr-only" for="bestaetigung-code">{{ t('bestaetigung.code') }}</label>
      <input
        id="bestaetigung-code"
        ref="feld"
        v-model="code"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="7"
        class="mt-3 w-full rounded-md border border-line bg-bg px-3 py-2 text-center font-mono text-lg tracking-widest text-fg focus:border-primary focus:outline-none"
      >
      <p v-if="fehler" class="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">{{ fehler }}</p>
      <div class="mt-4 flex justify-end gap-2">
        <button type="button" class="rounded-md px-3 py-1.5 text-sm text-fg-mid hover:bg-hover" @click="store.abgebrochen()">{{ t('ui.abbrechen') }}</button>
        <button
          type="submit"
          :disabled="laeuft || !/^\d{6}$/.test(code.replace(/\s+/g, ''))"
          class="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40 hover:bg-primary-dark transition-colors"
        >{{ t('bestaetigung.weiter') }}</button>
      </div>
    </form>
  </div>
</template>
